/**
 * Authentication error message utility
 * Maps technical error codes to user-friendly messages
 */

/**
 * Get user-friendly error message from error object
 */
export function getErrorMessage(error: any): string {
  // Handle Firebase Auth error codes
  if (error?.code) {
    switch (error.code) {
      // Authentication errors
      case 'auth/user-not-found':
        return 'No account found with this email address. Please check your email or create a new account.';
      
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again or reset your password.';
      
      case 'auth/invalid-credential':
        return 'Invalid email or password. Please check your credentials and try again.';
      
      case 'auth/invalid-email':
        return 'Invalid email address. Please enter a valid email address.';
      
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support for assistance.';
      
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please wait a few minutes and try again.';
      
      case 'auth/email-already-in-use':
        return 'An account with this email already exists. Please sign in instead.';
      
      case 'auth/weak-password':
        return 'Password is too weak. Please use at least 6 characters with a mix of letters and numbers.';
      
      case 'auth/operation-not-allowed':
        return 'Email/password accounts are not enabled. Please contact support.';
      
      case 'auth/requires-recent-login':
        return 'This operation requires recent authentication. Please sign out and sign in again.';
      
      // Network errors
      case 'auth/network-request-failed':
        return 'Network error. Please check your internet connection and try again.';
      
      // OAuth errors
      case 'auth/popup-closed-by-user':
        return 'Sign-in was cancelled. Please try again.';
      
      case 'auth/popup-blocked':
        return 'Popup was blocked by your browser. Please allow popups for this site and try again.';
      
      case 'auth/cancelled-popup-request':
        return 'Only one popup request is allowed at a time. Please wait and try again.';
      
      case 'auth/invalid-client':
        return 'OAuth client is not configured. Please contact support.';
      
      // API Key and Configuration errors
      case 'auth/invalid-api-key':
        return 'Firebase API key is invalid. Please check your configuration or contact support.';
      
      case 'auth/unauthorized-domain':
        return 'This domain is not authorized for Firebase Authentication. Please contact support.';
      
      case 'auth/app-not-authorized':
        return 'This app is not authorized to use Firebase Authentication. Please contact support.';
      
      case 'auth/internal-error':
        return 'An internal error occurred. Please try again or contact support if the problem persists.';
      
      // Token errors
      case 'auth/id-token-expired':
        return 'Your session has expired. Please sign in again.';
      
      case 'auth/id-token-revoked':
        return 'Your session has been revoked. Please sign in again.';
      
      // API errors
      case 'NETWORK_ERROR':
        return 'Unable to connect to server. Please check your internet connection and try again.';
      
      case 'TIMEOUT':
        return 'Request timed out. Please check your connection and try again.';
      
      case 'AUTH_REFRESH_FAILED':
        return 'Failed to refresh your session. Please sign in again.';
      
      case 'PARSE_ERROR':
        return 'Server response error. Please try again.';
      
      default:
        // Check if it's a Firebase error code we haven't mapped
        if (error.code.startsWith('auth/')) {
          return `Authentication error: ${error.message || 'Please try again.'}`;
        }
    }
  }
  
  // Handle error messages directly
  if (error?.message) {
    const message = error.message.toLowerCase();
    
    // Network errors
    if (message.includes('failed to fetch') || message.includes('networkerror')) {
      return 'Network error. Please check your internet connection and try again.';
    }
    
    if (message.includes('timeout') || message.includes('timed out')) {
      return 'Request timed out. Please check your connection and try again.';
    }
    
    // Session errors
    if (message.includes('session') && message.includes('failed')) {
      return 'Session creation failed. You can still continue with your account.';
    }
    
    // Return the message if it seems user-friendly
    if (message.length < 100 && !message.includes('error:') && !message.includes('exception')) {
      return error.message;
    }
  }
  
  // Default fallback
  return 'An unexpected error occurred. Please try again.';
}

/**
 * Check if error is a network error (retryable)
 */
export function isNetworkError(error: any): boolean {
  if (error?.code) {
    return error.code === 'auth/network-request-failed' || 
           error.code === 'NETWORK_ERROR' || 
           error.code === 'TIMEOUT';
  }
  
  if (error?.message) {
    const message = error.message.toLowerCase();
    return message.includes('failed to fetch') || 
           message.includes('networkerror') || 
           message.includes('timeout');
  }
  
  return false;
}

/**
 * Check if error is a session API error (non-critical)
 */
export function isSessionApiError(error: any): boolean {
  if (error?.message) {
    const message = error.message.toLowerCase();
    return message.includes('session') && 
           (message.includes('failed') || message.includes('timeout') || message.includes('error'));
  }
  
  return false;
}

